// Admin Dashboard Main Page

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { dashboardAPI } from '../../services/api';
import { resolveImageUrl } from '../../utils/imageUrl';
import {
  Package,
  Layers,
  Image as ImageIcon,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Star
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await dashboardAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#055509]"></div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    {
      title: 'Total de Produtos',
      value: stats?.products?.total || 0,
      icon: Package,
      color: 'bg-blue-500',
      link: '/dashboard/produtos'
    },
    {
      title: 'Produtos Ativos',
      value: stats?.products?.active || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      link: '/dashboard/produtos?active=true'
    },
    {
      title: 'Produtos em Destaque',
      value: stats?.products?.featured || 0,
      icon: Star,
      color: 'bg-yellow-500',
      link: '/dashboard/produtos?featured=true'
    },
    {
      title: 'Categorias',
      value: stats?.categories?.total || 0,
      icon: Layers,
      color: 'bg-purple-500',
      link: '/dashboard/categorias'
    },
    {
      title: 'Banners Ativos',
      value: stats?.banners?.active || 0,
      icon: ImageIcon,
      color: 'bg-pink-500',
      link: '/dashboard/banners'
    },
    {
      title: 'Arquivos de Mídia',
      value: stats?.media?.total || 0,
      icon: ImageIcon,
      color: 'bg-indigo-500',
      link: '/dashboard/midia'
    }
  ];

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Dashboard</h1>
        <p className="text-gray-600 mb-8">
          Visão geral do sistema TerraHydro
        </p>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link key={index} to={stat.link}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          {stat.title}
                        </p>
                        <p className="text-3xl font-bold text-gray-800">
                          {stat.value}
                        </p>
                      </div>
                      <div className={`${stat.color} p-3 rounded-lg`}>
                        <Icon className="text-white" size={24} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Recent Products */}
        {stats?.recentProducts && stats.recentProducts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Produtos Recentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.recentProducts.map((product) => (
                  <Link
                    key={product.id}
                    to={`/dashboard/produtos/${product.id}`}
                    className="flex items-center gap-4 p-4 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    {product.mainImage ? (
                      <img
                        src={resolveImageUrl(product.mainImage)}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Package className="text-gray-400" size={24} />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800">{product.name}</div>
                      <div className="text-sm text-gray-600">
                        Código: {product.code} • {product.category}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {product.featured && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                          Destaque
                        </span>
                      )}
                      {product.active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded">
                          Inativo
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Link to="/dashboard/produtos/novo">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer hover:border-[#055509]">
              <CardContent className="p-6 text-center">
                <Package className="mx-auto mb-2 text-[#055509]" size={32} />
                <h3 className="font-semibold text-gray-800">Novo Produto</h3>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/dashboard/importar">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer hover:border-[#FFB025]">
              <CardContent className="p-6 text-center">
                <TrendingUp className="mx-auto mb-2 text-[#FFB025]" size={32} />
                <h3 className="font-semibold text-gray-800">Importar Produtos</h3>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/dashboard/midia">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer hover:border-purple-500">
              <CardContent className="p-6 text-center">
                <ImageIcon className="mx-auto mb-2 text-purple-500" size={32} />
                <h3 className="font-semibold text-gray-800">Biblioteca de Mídia</h3>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/dashboard/configuracoes">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer hover:border-gray-500">
              <CardContent className="p-6 text-center">
                <AlertCircle className="mx-auto mb-2 text-gray-500" size={32} />
                <h3 className="font-semibold text-gray-800">Configurações</h3>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
